import type { LevelSpec } from './types';

// Difficulty curve: 0..1 over 100 levels. Slight S-curve.
export function difficultyFor(level: number): number {
  const t = (level - 1) / 99;
  // Smooth-step.
  return t * t * (3 - 2 * t);
}

export function tierFor(level: number): LevelSpec['tier'] {
  if (level <= 10) return 'tutorial';
  if (level <= 30) return 'normal';
  if (level <= 50) return 'hard';
  if (level <= 70) return 'expert';
  if (level <= 90) return 'master';
  return 'legendary';
}

// Enemy stat scaling by difficulty. Multipliers.
export interface DifficultyMods {
  hp: number;
  speed: number;
  fireRate: number;
  bulletSpeed: number;
  spawnDensity: number;
  rewardMul: number;
}

/**
 * Enemy HP multiplier — piecewise linear so progression is explicit and
 * player-readable instead of opaquely curved through smoothstep+ceil.
 *
 *   L1   → 1.0×   (spec HP, no boost)
 *   L20  → 2.0×   (double from L1)
 *   L50  → 3.0×   (triple from L1)
 *   L>50 → 3.0×   capped — higher tiers bring tougher enemy KINDS
 *                 (sniper, splitter, juggernaut etc.) so the multiplier
 *                 doesn't have to keep climbing as well.
 *
 * Used in place of the old `1 + d * 3.5` smoothstep, which combined with
 * `Math.ceil` at the call-site spawned 2-HP grunts from L2 onwards (any
 * multiplier > 1.0 ceils to 2 for a spec.hp=1 enemy).
 */
export function hpMultFor(level: number): number {
  if (level <= 1)  return 1;
  if (level <= 20) return 1 + (level - 1)  / 19;        // L1=1, L20=2
  if (level <= 50) return 2 + (level - 20) / 30;        // L20=2, L50=3
  return 3;                                             // L>50 capped
}

export function modsFor(level: number): DifficultyMods {
  const d = difficultyFor(level);
  return {
    hp: hpMultFor(level),
    speed: 0.9 + d * 1.1,
    // Global enemy fire-rate multiplier. Applied in Game.ts as
    //   e.fireCooldown = 1 / (e.spec.fireRate * m.fireRate)
    // so halving this doubles the interval between enemy shots.
    //
    // Base formula was `1.3 + d * 1.6` (0.65-1.45 rps for tutorial grunts;
    // higher for later tiers). Reduced by 50 % on player request — too
    // many bullets on-screen at once was making mid-game feel like
    // bullet-hell rather than Galaxian. Bosses are NOT affected: they
    // fire on hand-tuned per-phase timers in boss.ts, not via mods.
    fireRate: (1.3 + d * 1.6) * 0.5,
    bulletSpeed: 0.9 + d * 0.8,
    spawnDensity: 1 + d * 1.4,
    rewardMul: 1 + d * 4,
  };
}

// $SKR base reward for level, halving across emission years.
// Year 0 reference. Engine reads halving from chain.
export function skrRewardForLevel(level: number): number {
  // Linear ramp 5 -> 80 across 1..100.
  return Math.round(5 + (75 * (level - 1)) / 99);
}

// ---- Deterministic difficulty schedule ----
// Galaxian-style: each level opens with a large swarm (the "first wave"), then
// follow-up waves attack as the swarm thins. Wave count steps up every 20 levels.
//
//   L1   → 50 enemies, 2 waves   (swarm 35 + follow-up 15)
//   L20  → 78 enemies, 2 waves
//   L21  → 78 enemies, 3 waves
//   L40  → 113 enemies, 3 waves
//   L41  → 114 enemies, 4 waves
//   L99  → 234 enemies, 6 waves
//   L100 → boss (intro + Sovereign)
//
// Boss levels (every 10th) keep their short intro + boss schedule.
export function targetEnemiesForLevel(level: number): number {
  if (level % 10 === 0) return BOSS_INTRO_ENEMIES;
  const x = level - 1;
  // -10 % vs. the old base+slope (user feedback: too dense on mobile).
  // Multiplier preserves the original growth shape:
  // L1 ≈ 59, L20 ≈ 87, L40 ≈ 125, L100 ≈ 280 (was 65/97/139/311).
  return Math.round((65 + 1.5 * x + 0.01 * x * x) * 0.9);
}

// Boss-intro wave also scaled down by 10 % so the LV10/20/… opener feels
// in line with the rest of the curve.
export const BOSS_INTRO_ENEMIES = 11;

//   L1-29   → 1   (single swarm — no end-of-level trickle)
//   L30-59  → 2
//   L60-89  → 3
//   L90-100 → 4
export function wavesForLevel(level: number): number {
  if (level % 10 === 0) return 1;            // boss-intro wave only
  return Math.min(4, 1 + Math.floor(level / 30));
}

// Budget split per wave. The first wave is intentionally the *swarm* — most of
// the enemies arrive at once and the level pressure decays from there.
// Returned weights are normalised by the caller; relative magnitudes matter.
export function waveWeights(numWaves: number): number[] {
  const w: number[] = [];
  for (let i = 0; i < numWaves; i++) {
    // First wave is the dominant swarm; later waves shrink hyperbolically.
    w.push(i === 0 ? 1.8 : 1 / (1 + i * 0.55));
  }
  return w;
}

